import type { BenchmarkRowT, VerdictRowT } from '../../shared/contract/analysis.js';

export interface SupplierLead {
  benchmarkId: string;
  lineItemId: string;
  name: string;
  location: string;
  country: string;
  origin: 'local' | 'import';
  url: string;
  price: number;
  currency: string;
  unit: string;
  matchQuality: string | null;
}

const MAX_PER_LINE = 3;
const matchRank = (match: string | null): number => (match === 'exact' ? 0 : match === 'equivalent' ? 1 : 2);
const supplierKey = (name: string): string =>
  name
    .normalize('NFKC')
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '');

/** Only seller-identified, cited benchmarks become leads; keep one best listing per seller and line. */
export function supplierLeads(
  benchmarks: readonly BenchmarkRowT[],
  verdicts: readonly VerdictRowT[],
  localCountry = 'Pakistan',
): SupplierLead[] {
  const lineByVerdict = new Map(verdicts.map((verdict) => [verdict.id, verdict.lineItemId]));
  const candidates: SupplierLead[] = [];
  for (const benchmark of benchmarks) {
    const lineItemId = lineByVerdict.get(benchmark.lineVerdictId);
    const name = benchmark.supplierName?.trim();
    const price = Number(benchmark.price);
    if (
      lineItemId === undefined ||
      name === undefined ||
      name === '' ||
      benchmark.price === null ||
      benchmark.sourceUrl === null ||
      !Number.isFinite(price)
    )
      continue;
    let url: URL;
    try {
      url = new URL(benchmark.sourceUrl);
    } catch {
      continue;
    }
    if (!['http:', 'https:'].includes(url.protocol)) continue;
    const namedLocation = benchmark.supplierLocation?.trim();
    const geography = benchmark.geography?.trim();
    const location = namedLocation !== undefined && namedLocation !== '' ? namedLocation : geography;
    if (location === undefined || location.toLocaleLowerCase() === 'global' || supplierKey(name) === '') continue;
    const country = (
      geography !== undefined && geography !== '' && geography.toLocaleLowerCase() !== 'global' ? geography : location
    )
      .split(',')
      .at(-1)
      ?.trim();
    if (country === undefined || country === '' || country.toLocaleLowerCase() === 'global') continue;
    const locationCountry = namedLocation?.includes(',') === true ? namedLocation.split(',').at(-1)?.trim() : undefined;
    if (locationCountry !== undefined && locationCountry.toLocaleLowerCase() !== country.toLocaleLowerCase()) continue;
    const origin = country.toLocaleLowerCase() === localCountry.toLocaleLowerCase() ? 'local' : 'import';
    candidates.push({
      benchmarkId: benchmark.id,
      lineItemId,
      name,
      location,
      country,
      origin,
      url: url.href,
      price,
      currency: benchmark.currency ?? '',
      unit: benchmark.unit ?? '',
      matchQuality: benchmark.matchQuality,
    });
  }
  const byLine = new Map<string, SupplierLead[]>();
  for (const candidate of candidates) {
    const list = byLine.get(candidate.lineItemId) ?? [];
    list.push(candidate);
    byLine.set(candidate.lineItemId, list);
  }
  return [...byLine.values()].flatMap((rows) => {
    const bestBySupplier = new Map<string, SupplierLead>();
    for (const row of rows) {
      const key = supplierKey(row.name);
      const prior = bestBySupplier.get(key);
      if (prior === undefined || matchRank(row.matchQuality) < matchRank(prior.matchQuality))
        bestBySupplier.set(key, row);
    }
    const ranked = [...bestBySupplier.values()].sort(
      (a, b) => matchRank(a.matchQuality) - matchRank(b.matchQuality) || a.name.localeCompare(b.name),
    );
    const local = ranked.filter((row) => row.origin === 'local');
    const imported = ranked.filter((row) => row.origin === 'import');
    const selected: SupplierLead[] = [];
    if (local[0] !== undefined && imported[0] !== undefined) selected.push(local[0], imported[0]);
    for (const row of ranked) {
      if (selected.length >= MAX_PER_LINE) break;
      if (!selected.includes(row)) selected.push(row);
    }
    return selected.sort((a, b) => (a.origin === b.origin ? 0 : a.origin === 'local' ? -1 : 1));
  });
}
